/**
 * AdminPostForm - Create / edit a city post
 * @atomic @idempotent
 */
import { memo, useCallback } from 'react'
import { getInputClasses, getSelectClasses, getTextareaClasses, labelClasses, errorMessageClasses } from '../../styles/inputStyles'

const LANGUAGES = ['en', 'es-MX', 'fr-CA', 'pt-BR', 'ru', 'zh-CN']
const POST_TYPES = ['post', 'event', 'guide', 'spotlight']

const AdminPostForm = memo(function AdminPostForm({ form, onChange, errors = {}, onSubmit, onCancel, submitting }) {
  const set = useCallback((key) => (e) => {
    onChange((f) => ({ ...f, [key]: e.target.value }))
  }, [onChange])

  const setCheck = useCallback((key) => (e) => {
    onChange((f) => ({ ...f, [key]: e.target.checked }))
  }, [onChange])

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit() }} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Language */}
        <div>
          <label className={labelClasses}>Language</label>
          <select value={form.language || 'en'} onChange={set('language')} className={getSelectClasses()}>
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </select>
        </div>

        {/* Post type */}
        <div>
          <label className={labelClasses}>Post Type</label>
          <select value={form.post_type || 'post'} onChange={set('post_type')} className={getSelectClasses()}>
            {POST_TYPES.map((t) => (
              <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Slug */}
        <div>
          <label className={labelClasses}>Slug</label>
          <input
            type="text"
            value={form.slug || ''}
            onChange={set('slug')}
            placeholder="my-post-slug"
            className={getInputClasses(!!errors.slug)}
          />
          {errors.slug && <p className={errorMessageClasses}>{errors.slug}</p>}
        </div>

        {/* Title */}
        <div>
          <label className={labelClasses}>Title</label>
          <input
            type="text"
            value={form.title || ''}
            onChange={set('title')}
            placeholder="Post title"
            className={getInputClasses(!!errors.title)}
          />
          {errors.title && <p className={errorMessageClasses}>{errors.title}</p>}
        </div>
      </div>

      {/* Excerpt */}
      <div>
        <label className={labelClasses}>Excerpt (optional)</label>
        <textarea
          value={form.excerpt || ''}
          onChange={set('excerpt')}
          rows={2}
          placeholder="Short summary shown in listings…"
          className={getTextareaClasses()}
        />
      </div>

      {/* Content */}
      <div>
        <label className={labelClasses}>Content</label>
        <textarea
          value={form.content || ''}
          onChange={set('content')}
          rows={8}
          placeholder="Full post content (Markdown supported)…"
          className={getTextareaClasses()}
        />
        {errors.content && <p className={errorMessageClasses}>{errors.content}</p>}
      </div>

      {/* Image URL */}
      <div>
        <label className={labelClasses}>Image URL (optional)</label>
        <input
          type="url"
          value={form.image_url || ''}
          onChange={set('image_url')}
          placeholder="https://…"
          className={getInputClasses(false)}
        />
      </div>

      {/* Translation group */}
      <div>
        <label className={labelClasses}>Translation Group ID (optional — link to existing translations)</label>
        <input
          type="text"
          value={form.translation_group_id || ''}
          onChange={set('translation_group_id')}
          placeholder="UUID of the original post (leave blank to create new group)"
          className={getInputClasses(false)}
        />
      </div>

      {/* Flags */}
      <div className="flex gap-6">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={!!form.is_published} onChange={setCheck('is_published')} className="rounded border-gray-300 text-primary focus:ring-primary" />
          Publish immediately
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={!!form.is_pinned} onChange={setCheck('is_pinned')} className="rounded border-gray-300 text-primary focus:ring-primary" />
          Pin post
        </label>
      </div>

      <div className="flex gap-3 pt-2 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="px-4 py-2 text-sm bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50">
          {submitting ? 'Saving…' : 'Save Post'}
        </button>
      </div>
    </form>
  )
})

export default AdminPostForm
